import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import {
  getGetContractQueryKey,
  getListContractsQueryKey,
  getListContractNegotiationItemsQueryKey,
  useListContractNegotiationItems,
  useCreateContractNegotiationItem,
  useUpdateContractNegotiationItem,
  useDeleteContractNegotiationItem,
} from "@workspace/api-client-react";
import type { NegotiationItem } from "@workspace/api-client-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { format } from "date-fns";
import { MessageSquarePlus, Pencil, Trash2, X, Save, CheckCircle, XCircle, Clock, MinusCircle } from "lucide-react";

const STATUS_CONFIG = {
  open: { label: "Open", icon: Clock, cls: "bg-amber-50 text-amber-700 border-amber-200" },
  accepted: { label: "Accepted", icon: CheckCircle, cls: "bg-green-50 text-green-700 border-green-200" },
  rejected: { label: "Rejected", icon: XCircle, cls: "bg-red-50 text-red-700 border-red-200" },
  deferred: { label: "Deferred", icon: MinusCircle, cls: "bg-slate-50 text-slate-600 border-slate-200" },
};

const FIELD_REF_OPTIONS = [
  { value: "annualSalary", label: "Annual Salary" },
  { value: "signingBonus", label: "Signing Bonus" },
  { value: "buyoutClause", label: "Buyout Clause" },
  { value: "startDate", label: "Start Date" },
  { value: "endDate", label: "End Date" },
  { value: "contractType", label: "Contract Type" },
  { value: "terminationConditions", label: "Termination Conditions" },
  { value: "bonuses", label: "Bonuses" },
  { value: "allowances", label: "Allowances" },
];

interface NegotiationTrackerProps {
  contractId: number;
  isAdmin: boolean;
}

type NegotiationStatus = "open" | "accepted" | "rejected" | "deferred";

interface FormState {
  raisedBy: string;
  summary: string;
  response: string;
  resolution: string;
  status: NegotiationStatus;
  fieldRef: string;
  clauseRef: string;
}

const EMPTY_FORM: FormState = {
  raisedBy: "",
  summary: "",
  response: "",
  resolution: "",
  status: "open",
  fieldRef: "",
  clauseRef: "",
};

export function NegotiationTracker({ contractId, isAdmin }: NegotiationTrackerProps) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [form, setForm] = useState<FormState>(EMPTY_FORM);

  const itemsQueryKey = getListContractNegotiationItemsQueryKey(contractId);
  const { data: items = [], isLoading } = useListContractNegotiationItems(contractId, {
    query: { queryKey: itemsQueryKey, enabled: !!contractId },
  });

  const invalidateAll = () => {
    void queryClient.invalidateQueries({ queryKey: itemsQueryKey });
    void queryClient.invalidateQueries({ queryKey: getGetContractQueryKey(contractId) });
    void queryClient.invalidateQueries({ queryKey: getListContractsQueryKey() });
  };

  const createItem = useCreateContractNegotiationItem();
  const updateItem = useUpdateContractNegotiationItem();
  const deleteItem = useDeleteContractNegotiationItem();
  const saving = createItem.isPending || updateItem.isPending;

  const errorMessage = (err: unknown, fallback: string) => {
    if (err && typeof err === "object" && "data" in err) {
      const data = (err as { data: unknown }).data;
      if (data && typeof data === "object" && "error" in data) {
        const e = (data as { error?: unknown }).error;
        if (typeof e === "string") return e;
      }
    }
    return err instanceof Error ? err.message : fallback;
  };

  const handleSubmit = async () => {
    if (!form.raisedBy.trim() || !form.summary.trim()) return;
    const payload = {
      raisedBy: form.raisedBy,
      summary: form.summary,
      response: form.response || null,
      resolution: form.resolution || null,
      status: form.status,
      fieldRef: form.fieldRef || null,
      clauseRef: form.clauseRef || null,
    };
    try {
      if (editingId !== null) {
        await updateItem.mutateAsync({ contractId, itemId: editingId, data: payload });
        toast({ title: "Item updated" });
      } else {
        await createItem.mutateAsync({ contractId, data: payload });
        toast({ title: "Negotiation item logged" });
      }
      invalidateAll();
      setForm(EMPTY_FORM);
      setShowForm(false);
      setEditingId(null);
    } catch (err) {
      toast({ title: "Error", description: errorMessage(err, "Failed to save"), variant: "destructive" });
    }
  };

  const handleEdit = (item: NegotiationItem) => {
    setForm({
      raisedBy: item.raisedBy,
      summary: item.summary,
      response: item.response ?? "",
      resolution: item.resolution ?? "",
      status: item.status,
      fieldRef: item.fieldRef ?? "",
      clauseRef: item.clauseRef ?? "",
    });
    setEditingId(item.id);
    setShowForm(true);
  };

  const handleDelete = async (id: number) => {
    if (!confirm("Delete this negotiation item?")) return;
    try {
      await deleteItem.mutateAsync({ contractId, itemId: id });
      invalidateAll();
      toast({ title: "Item deleted" });
    } catch (err) {
      toast({ title: "Error", description: errorMessage(err, "Could not delete item"), variant: "destructive" });
    }
  };

  const handleStatusQuickChange = async (id: number, status: NegotiationStatus) => {
    try {
      await updateItem.mutateAsync({ contractId, itemId: id, data: { status } });
      invalidateAll();
    } catch (err) {
      toast({ title: "Error", description: errorMessage(err, "Could not update status"), variant: "destructive" });
    }
  };

  const cancelForm = () => {
    setForm(EMPTY_FORM);
    setShowForm(false);
    setEditingId(null);
  };

  const openCount = items.filter((i) => i.status === "open").length;

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <CardTitle className="text-lg flex items-center gap-2">
            <MessageSquarePlus className="h-5 w-5 text-muted-foreground" />
            Negotiation Tracker
            {openCount > 0 && (
              <Badge variant="outline" className="text-xs bg-amber-50 text-amber-700 border-amber-200">
                {openCount} open
              </Badge>
            )}
          </CardTitle>
          {isAdmin && !showForm && (
            <Button size="sm" onClick={() => setShowForm(true)}>
              <MessageSquarePlus className="h-3.5 w-3.5 mr-1.5" /> Log Item
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {showForm && (
          <div className="border rounded-lg p-4 space-y-3 bg-muted/30">
            <div className="text-sm font-medium">{editingId !== null ? "Edit negotiation item" : "Log negotiation item"}</div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-muted-foreground mb-1 block">Raised by *</label>
                <Input
                  placeholder="Coach, Agent, Athletic Director…"
                  value={form.raisedBy}
                  onChange={(e) => setForm((f) => ({ ...f, raisedBy: e.target.value }))}
                  disabled={saving}
                />
              </div>
              <div>
                <label className="text-xs text-muted-foreground mb-1 block">Status</label>
                <Select value={form.status} onValueChange={(v) => setForm((f) => ({ ...f, status: v as NegotiationStatus }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="open">Open</SelectItem>
                    <SelectItem value="accepted">Accepted</SelectItem>
                    <SelectItem value="rejected">Rejected</SelectItem>
                    <SelectItem value="deferred">Deferred</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <label className="text-xs text-muted-foreground mb-1 block">Item summary *</label>
              <Textarea
                placeholder="e.g. Agent requested 6-year term instead of 5-year…"
                value={form.summary}
                onChange={(e) => setForm((f) => ({ ...f, summary: e.target.value }))}
                className="min-h-[80px]"
                disabled={saving}
              />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-muted-foreground mb-1 block">Relates to field</label>
                <Select value={form.fieldRef || "__none"} onValueChange={(v) => setForm((f) => ({ ...f, fieldRef: v === "__none" ? "" : v }))}>
                  <SelectTrigger><SelectValue placeholder="— none —" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none">— none —</SelectItem>
                    {FIELD_REF_OPTIONS.map((o) => (
                      <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-xs text-muted-foreground mb-1 block">Relates to clause</label>
                <Input
                  placeholder="e.g. Buyout clause, NIL rights…"
                  value={form.clauseRef}
                  onChange={(e) => setForm((f) => ({ ...f, clauseRef: e.target.value }))}
                  disabled={saving}
                />
              </div>
            </div>
            <div>
              <label className="text-xs text-muted-foreground mb-1 block">Our response</label>
              <Textarea
                placeholder="How did the institution respond?…"
                value={form.response}
                onChange={(e) => setForm((f) => ({ ...f, response: e.target.value }))}
                className="min-h-[60px]"
                disabled={saving}
              />
            </div>
            <div>
              <label className="text-xs text-muted-foreground mb-1 block">Resolution</label>
              <Textarea
                placeholder="Final outcome or agreement…"
                value={form.resolution}
                onChange={(e) => setForm((f) => ({ ...f, resolution: e.target.value }))}
                className="min-h-[60px]"
                disabled={saving}
              />
            </div>
            <div className="flex gap-2 justify-end">
              <Button variant="ghost" size="sm" onClick={cancelForm} disabled={saving}>
                <X className="h-3.5 w-3.5 mr-1" /> Cancel
              </Button>
              <Button size="sm" onClick={handleSubmit} disabled={saving || !form.raisedBy.trim() || !form.summary.trim()}>
                <Save className="h-3.5 w-3.5 mr-1.5" />
                {saving ? "Saving…" : editingId !== null ? "Save changes" : "Log item"}
              </Button>
            </div>
          </div>
        )}

        {isLoading ? (
          <div className="text-sm text-muted-foreground py-6 text-center animate-pulse">Loading…</div>
        ) : items.length === 0 ? (
          <div className="text-sm text-muted-foreground py-8 text-center">
            <MessageSquarePlus className="h-6 w-6 mx-auto mb-2 opacity-30" />
            No negotiation items logged yet.{isAdmin && " Click \"Log Item\" to record something the coach or agent raised."}
          </div>
        ) : (
          <div className="space-y-3">
            {items.map((item) => {
              const sc = STATUS_CONFIG[item.status];
              const Icon = sc.icon;
              return (
                <div key={item.id} className="border rounded-lg p-3 space-y-2">
                  <div className="flex items-start justify-between gap-2 flex-wrap">
                    <div className="flex items-center gap-2 flex-wrap">
                      <Badge variant="outline" className={`text-xs gap-1 ${sc.cls}`}>
                        <Icon className="h-3 w-3" /> {sc.label}
                      </Badge>
                      <span className="text-sm font-medium">{item.raisedBy}</span>
                      {(item.fieldRef || item.clauseRef) && (
                        <span className="text-xs text-muted-foreground">
                          re: {item.fieldRef ? FIELD_REF_OPTIONS.find((o) => o.value === item.fieldRef)?.label ?? item.fieldRef : item.clauseRef}
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                      {isAdmin && item.status === "open" && (
                        <>
                          <Button variant="ghost" size="sm" className="h-6 px-1.5 text-xs text-green-700 hover:bg-green-50" onClick={() => handleStatusQuickChange(item.id, "accepted")}>
                            <CheckCircle className="h-3 w-3 mr-0.5" /> Accept
                          </Button>
                          <Button variant="ghost" size="sm" className="h-6 px-1.5 text-xs text-red-700 hover:bg-red-50" onClick={() => handleStatusQuickChange(item.id, "rejected")}>
                            <XCircle className="h-3 w-3 mr-0.5" /> Reject
                          </Button>
                        </>
                      )}
                      {isAdmin && (
                        <>
                          <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => handleEdit(item)}>
                            <Pencil className="h-3 w-3" />
                          </Button>
                          <Button variant="ghost" size="icon" className="h-6 w-6 text-destructive hover:bg-destructive/10" onClick={() => handleDelete(item.id)}>
                            <Trash2 className="h-3 w-3" />
                          </Button>
                        </>
                      )}
                    </div>
                  </div>
                  <p className="text-sm text-foreground leading-relaxed">{item.summary}</p>
                  {item.response && (
                    <div className="text-xs bg-muted/50 rounded p-2">
                      <span className="font-medium text-muted-foreground">Response: </span>{item.response}
                    </div>
                  )}
                  {item.resolution && (
                    <div className="text-xs bg-green-50 rounded p-2 text-green-800">
                      <span className="font-medium">Resolution: </span>{item.resolution}
                    </div>
                  )}
                  <div className="text-xs text-muted-foreground">
                    Logged {format(new Date(item.createdAt), "MMM d, yyyy h:mm a")}
                    {item.updatedAt !== item.createdAt && ` · Updated ${format(new Date(item.updatedAt), "MMM d, yyyy")}`}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
